import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { FiX, FiArrowRight, FiArrowLeft, FiCheck, FiUsers, FiPlusCircle, FiSettings, FiEdit3, FiMessageSquare } from 'react-icons/fi';
import { BsRocket, BsLightbulb } from 'react-icons/bs';
import { useGlobal } from '../Context/GlobalContext';
import { useLanguage } from '../Context/LanguageContext';
import './HumanSupportTour.css';
import OutlinedSecondaryButton from './Buttons/OutlinedSecondaryButton';
import LinkButton from './Buttons/LinkButton';
import PrimaryButton from './Buttons/PrimaryButton';

const HumanSupportTour = ({ show, onClose, onComplete, chatbotId }) => {
  const { t } = useLanguage();
  const { contentDomain } = useGlobal();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Construct GIF URL with fallback
  const gifUrl = useMemo(() => {
    const domain = contentDomain || 'https://content.brandvoxai.com';
    return `${domain}/assets/images/action-map-gifs/human-support.gif`;
  }, [contentDomain]);

  useEffect(() => {
    if (!show) return;

    // Cleanup on unmount or when modal closes
    return () => {
      const highlightedElements = document.querySelectorAll('.tour-highlight-pulse');
      highlightedElements.forEach(el => el.classList.remove('tour-highlight-pulse'));
    };
  }, [show]);

  const slides = [
    {
      title: t('components.humansupporttour.welcomeTitle'),
      description: t('components.humansupporttour.welcomeDescription'),
      image: null,
      imageAlt: 'Welcome illustration',
      noImage: true,
      compact: true,
      icon: FiUsers
    },
    {
      title: t('components.humansupporttour.step1Title'),
      description: t('components.humansupporttour.step1Description'),
      image: gifUrl,
      imageAlt: 'Enabling Human Support in Action Map',
      highlight: t('components.humansupporttour.step1Highlight'),
      icon: FiPlusCircle
    },
    {
      title: t('components.humansupporttour.step2Title'),
      description: t('components.humansupporttour.step2Description'),
      image: gifUrl,
      imageAlt: 'Configuring Human Support settings',
      highlight: t('components.humansupporttour.step2Highlight'),
      icon: FiSettings
    },
    {
      title: t('components.humansupporttour.step3Title'),
      description: t('components.humansupporttour.step3Description'),
      image: gifUrl,
      imageAlt: 'Setting up lead capture form',
      highlight: t('components.humansupporttour.step3Highlight'),
      icon: FiEdit3
    },
    {
      title: t('components.humansupporttour.step4Title'),
      description: t('components.humansupporttour.step4Description'),
      image: null,
      imageAlt: 'Responding to escalations',
      highlight: t('components.humansupporttour.step4Highlight'),
      noImage: true,
      compact: true,
      icon: FiMessageSquare
    },
    {
      title: t('components.humansupporttour.finalTitle'),
      description: t('components.humansupporttour.finalDescription'),
      image: null,
      imageAlt: 'Setup complete',
      cta: true,
      noImage: true,
      compact: true,
      icon: BsRocket,
      showDocsLink: true
    }
  ];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const handleFinish = () => {
    onComplete();
  };

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;
  const isFirstSlide = currentSlide === 0;

  return (
    <>
      {/* Custom backdrop */}
      {show && (
        <div className="tour-custom-backdrop" />
      )}

      <Modal
        show={show}
        onHide={handleSkip}
        centered
        size="lg"
        className="human-support-tour-modal"
        backdrop={false}
      >
        <Modal.Header className="border-0 pb-0">
          <button
            className="tour-close-btn"
            onClick={handleSkip}
            aria-label={t('components.humansupporttour.closeTour')}
          >
            <FiX size={20} />
          </button>
        </Modal.Header>

        <Modal.Body className="pt-2">
          <div className={`tour-slide-container ${currentSlideData.compact ? 'compact' : ''}`}>
            {/* Slide content */}
            <div className="tour-slide-content">
              <div className="tour-slide-title-container">
                {currentSlideData.icon && (
                  <currentSlideData.icon className="tour-slide-icon" />
                )}
                <h3 className="tour-slide-title">{currentSlideData.title}</h3>
              </div>
              <p className="tour-slide-description">{currentSlideData.description}</p>

              {currentSlideData.highlight && (
                <div className="tour-slide-highlight">
                  <BsLightbulb className="highlight-icon" />
                  <span>{currentSlideData.highlight}</span>
                </div>
              )}

              {/* Image/GIF placeholder */}
              {!currentSlideData.noImage && (
                <div className="tour-slide-image-container">
                  <img
                    src={currentSlideData.image}
                    alt={currentSlideData.imageAlt}
                    className="tour-slide-image"
                    onError={(e) => {
                      // Fallback if image doesn't exist yet
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="tour-slide-placeholder">
                    <FiUsers className="placeholder-icon" size={64} />
                    <p className="placeholder-text">{currentSlideData.imageAlt}</p>
                    <small className="placeholder-hint">
                      {t('components.humansupporttour.screenshotPlaceholder')}
                    </small>
                  </div>
                </div>
              )}

              {/* Documentation Link for final slide */}
              {currentSlideData.showDocsLink && (
                <div style={{
                  marginTop: '20px',
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <p style={{ marginBottom: '12px', fontSize: '14px', color: '#6c757d' }}>
                    {t('components.humansupporttour.learnMoreText')}
                  </p>
                  <a
                    href="https://brandvoxai.com/docs/support-bv/chats"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      backgroundColor: '#55297F',
                      color: 'white',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#441f66'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#55297F'}
                  >
                    {t('components.humansupporttour.viewFullDocumentation')} →
                  </a>
                </div>
              )}
            </div>

            {/* Progress dots */}
            <div className="tour-progress-dots">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`tour-dot ${index === currentSlide ? 'active' : ''} ${index < currentSlide ? 'completed' : ''}`}
                  onClick={() => handleDotClick(index)}
                  aria-label={t('components.humansupporttour.goToSlide', { number: index + 1 })}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="tour-navigation">
              <div className="tour-nav-left">
                {!isFirstSlide && (
                  <OutlinedSecondaryButton
                    onClick={handlePrev}
                    mobileWidth="100%"
                  >
                    <FiArrowLeft className="me-2" />
                    {t('components.humansupporttour.previous')}
                  </OutlinedSecondaryButton>
                )}
              </div>

              <div className="tour-nav-right">
                <LinkButton
                  className="tour-skip-btn"
                  onClick={handleSkip}
                  mobileWidth="100%"
                >
                  {t('components.humansupporttour.skipTour')}
                </LinkButton>

                {isLastSlide ? (
                  <PrimaryButton
                    mobileWidth="100%"
                    onClick={handleFinish}
                  >
                    <FiCheck className="me-2" />
                    {t('components.humansupporttour.getStarted')}
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    mobileWidth="100%"
                    onClick={handleNext}
                  >
                    {t('components.humansupporttour.next')}
                    <FiArrowRight className="ms-2" />
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default HumanSupportTour;
